import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Upload, Tag, Eye } from 'lucide-react';
import {
  createCategory,
  updateCategory,
  clearError,
  validateImageFile,
  selectCategories,
  selectCategoriesCreating,
  selectCategoriesUpdating,
  selectCategoriesError
} from '../redux/CategorySlice';

const CategoryForm = ({
  setShowModal,
  editingCategory,
  setEditingCategory
}) => {
  const dispatch = useDispatch();
  
  // Redux state
  const categories = useSelector(selectCategories);
  const isCreating = useSelector(selectCategoriesCreating);
  const isUpdating = useSelector(selectCategoriesUpdating);
  const error = useSelector(selectCategoriesError);

  // Local state
  const [formData, setFormData] = useState({
    name: '',
    status: 'Active'
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const statuses = ['Active', 'Inactive'];
  const isSubmitting = isCreating || isUpdating;

  // Initialize form when editing
  useEffect(() => {
    if (editingCategory) {
      // Get status value from the status object or use as-is
      const statusValue = editingCategory.status?.status || editingCategory.status || 'Active';
      
      setFormData({
        name: editingCategory.category || '', // Use 'category' field from API
        status: statusValue
      });
      setImagePreview(editingCategory.image || '');
      setImageFile(null);
      setIsViewMode(false);
    } else {
      // Reset form for new category
      setFormData({
        name: '',
        status: 'Active'
      });
      setImagePreview('');
      setImageFile(null);
      setIsViewMode(false);
    }
    setValidationErrors({});
    dispatch(clearError());
  }, [editingCategory, dispatch]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Category name must be at least 2 characters';
    }

    // Check for duplicate names (exclude current category if editing)
    const existingCategory = categories?.find(cat => 
      cat.category?.toLowerCase() === formData.name?.toLowerCase() && 
      (!editingCategory || cat.categoryid !== editingCategory.categoryid)
    );
    
    if (existingCategory) {
      newErrors.name = 'A category with this name already exists';
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageChange = (file) => {
    try {
      // Validate file
      validateImageFile(file);
      
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Clear any previous image errors
      setValidationErrors(prev => ({
        ...prev,
        image: ''
      }));
    } catch (error) {
      setValidationErrors(prev => ({
        ...prev,
        image: error.message
      }));
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageChange(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleImageChange(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setValidationErrors(prev => ({
      ...prev,
      image: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
  
    try {
      if (editingCategory) {
        // Update existing category
        await dispatch(updateCategory({
          id: editingCategory.categoryid,
          name: formData.name.trim(),
          status: formData.status,
          imageFile: imageFile, // null if no new image selected
          existingImageUrl: editingCategory.image // existing image URL
        })).unwrap();
      } else {
        // Create new category
        await dispatch(createCategory({
          name: formData.name.trim(),
          status: formData.status,
          imageFile: imageFile
        })).unwrap();
      }
  
      handleClose();
    } catch (error) {
      console.error('Failed to save category:', error);
    }
  };
  const handleClose = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      status: 'Active'
    });
    setImageFile(null);
    setImagePreview('');
    setValidationErrors({});
    setIsViewMode(false);
    dispatch(clearError());
  };

  const toggleViewMode = () => {
    setIsViewMode(!isViewMode);
  };

  return (
    <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <div className="bg-amber-100 p-2 rounded-lg">
            <Tag className="text-amber-600" size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {editingCategory ? (isViewMode ? 'View Category' : 'Edit Category') : 'Add New Category'}
            </h2>
            <p className="text-sm text-gray-500">
              {editingCategory ? (isViewMode ? 'Category details' : 'Update category information') : 'Create a new product category'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {editingCategory && (
            <button
              type="button"
              onClick={toggleViewMode}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title={isViewMode ? 'Edit Mode' : 'View Mode'}
              disabled={isSubmitting}
            >
              <Eye size={20} />
            </button>
          )}
          <button
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Redux Error Message */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Category Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={isViewMode || isSubmitting}
            placeholder="e.g., Scented Candles, Raw Candles, etc."
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed ${
              validationErrors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
          />
          {validationErrors.name && (
            <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
          )}
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            disabled={isViewMode || isSubmitting}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
          >
            {statuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category Image
          </label>
          
          {/* Image Preview */}
          {imagePreview && (
            <div className="mb-4">
              <div className="relative w-full h-48 bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={imagePreview}
                  alt="Category preview"
                  className="w-full h-full object-cover"
                />
                {!isViewMode && !isSubmitting && (
                  <button
                    type="button"
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          )}

          {!isViewMode && (
            <>
              {/* Drag & Drop Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragOver ? 'border-amber-500 bg-amber-50' : 'border-gray-300'
                } ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-2">
                  Drag and drop an image here, or click to select
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  className="hidden"
                  id="imageUpload"
                  disabled={isSubmitting}
                />
                <label
                  htmlFor="imageUpload"
                  className={`inline-block bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors ${
                    isSubmitting ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  }`}
                >
                  Choose File
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  Supported formats: JPEG, PNG, GIF, WebP (Max 5MB)
                </p>
              </div>

              {validationErrors.image && (
                <p className="mt-2 text-sm text-red-600">{validationErrors.image}</p>
              )}
            </>
          )}
        </div>

        {/* Action Buttons */}
        {!isViewMode && (
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              )}
              <span>
                {isSubmitting 
                  ? (editingCategory ? 'Updating...' : 'Creating...')
                  : (editingCategory ? 'Update Category' : 'Add Category')
                }
              </span>
            </button>
          </div>
        )}

        {/* View Mode Actions */}
        {isViewMode && editingCategory && (
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => setIsViewMode(false)}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            >
              Edit Category
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default CategoryForm;